/**
 * CORE-1298 — «первый слышимый кадр»: последнее звено цепочки
 * `клик → local publish/setEnabled → remote user_published → первый звук`.
 *
 * Первые три звена в телеметрии есть. Четвёртого нет вовсе: `subscribe()`
 * вернулся и `play()` вызван — это ещё не звук. Между ними лежат ровно те
 * причины, из-за которых тикет и заведён: пустой поток от SFU, дорожка,
 * снятая через десятки миллисекунд (CORE-1296), и блокировка автозапуска
 * браузером. Пока эту разницу никто не мерил, «unmute не сработал» и
 * «unmute сработал» приходят в базу одинаковыми.
 *
 * Меряем самым дешёвым из доступного — уровнем громкости, который Agora уже
 * считает для каждой удалённой дорожки (`getVolumeLevel()`, 0..1). Ни аудио, ни
 * его содержимое при этом не читается и никуда не уезжает: наружу идёт одно
 * число «через сколько миллисекунд впервые стало не ноль».
 *
 * Часы и сон принимаются аргументами, чтобы тест мерил логику, а не ждал
 * секунды реального времени.
 */

/**
 * Предел ожидания звука. Это и есть тот «измеримый предел» из приёмки: дольше
 * него молчание — уже не задержка, а факт для разбора. Четыре секунды покрывают
 * и перезапрос подписки, и повторный `play()` после жеста пользователя.
 */
export const REMOTE_AUDIBLE_TIMEOUT_MS = 4000;

/** Шаг опроса: мельче — дороже, крупнее — грубее замер задержки. */
export const REMOTE_AUDIBLE_POLL_MS = 200;

/**
 * Порог «звучит». `getVolumeLevel()` даёт 0 на тишине и заметно больше нуля на
 * речи; 0.01 отсекает шум округления, не требуя от человека говорить громко.
 */
export const REMOTE_AUDIBLE_LEVEL = 0.01;

export interface AudibleProbe {
    /** Пересёк ли уровень порог до истечения предела. */
    audible: boolean;
    /** Сколько ждали: до первого звука либо до предела. */
    waitedMs: number;
    /** Максимум увиденного уровня; `null` — уровень не читается вовсе. */
    peakLevel: number | null;
    /** Сколько раз успели опросить — отличает «не мерили» от «мерили и тихо». */
    polls: number;
}

export interface AudibleProbeOptions {
    /** Текущий уровень дорожки, `null`/`undefined` — не читается. */
    getLevel: () => number | null | undefined;
    now: () => number;
    sleep: (ms: number) => Promise<void>;
    timeoutMs?: number;
    pollMs?: number;
    threshold?: number;
}

export async function waitForAudibleLevel(o: AudibleProbeOptions): Promise<AudibleProbe> {
    const timeoutMs = o.timeoutMs ?? REMOTE_AUDIBLE_TIMEOUT_MS;
    const pollMs = o.pollMs ?? REMOTE_AUDIBLE_POLL_MS;
    const threshold = o.threshold ?? REMOTE_AUDIBLE_LEVEL;
    const startedAt = o.now();
    let peakLevel: number | null = null;
    let polls = 0;

    for (;;) {
        const raw = o.getLevel();
        polls += 1;
        if (typeof raw === 'number' && Number.isFinite(raw)) {
            peakLevel = peakLevel === null ? raw : Math.max(peakLevel, raw);
            if (raw >= threshold) {
                return { audible: true, waitedMs: o.now() - startedAt, peakLevel, polls };
            }
        }
        if (o.now() - startedAt >= timeoutMs) {
            return { audible: false, waitedMs: o.now() - startedAt, peakLevel, polls };
        }
        await o.sleep(pollMs);
    }
}
